import _ from 'lodash'
import { signIn } from '@/shared/services/session/compat'
import { useIsClient } from 'usehooks-ts'

import { useAuthContext } from '@/features/auth/model/auth-context'
import { Button } from '@/shared/ui'
import { AppIcon } from '@/shared/ui/icons'

/**
 * Provedores sociais.
 *
 * Consolida `SignIn/Providers` e `SignUp/Providers`, que eram o mesmo
 * componente duplicado — divergiam apenas no verbo do rótulo ("Entrar com" /
 * "Entre com") e no esqueleto de carregamento, que só uma das cópias tinha.
 */

const BRAND_LOGOS: Record<string, ReturnType<typeof AppIcon> | string> = {
  google: <AppIcon name="google" size={20} />,
  apple: <AppIcon name="apple" size={20} />,
  bjid: '/partners/brasiljunior/logo.png'
}

/**
 * Provedores que não são sociais: entram por credenciais, recuperação de sessão
 * ou pelo parceiro — nenhum deles vira botão nesta lista.
 */
export const NON_SOCIAL_PROVIDER_IDS = [
  'domain-sign-in',
  'refresh-session',
  'partner-provider'
]

type Provider = { id: string; name: string }

export const getSocialProviders = (providers: Record<string, unknown>) =>
  _.omit(providers, NON_SOCIAL_PROVIDER_IDS) as Record<string, Provider>

type SocialProvidersProps = {
  /** Verbo do rótulo. O cadastro usa "Entre com"; o acesso, "Entrar com". */
  label?: (providerName: string) => string
}

export function SocialProviders({
  label = name => `Entrar com ${name}`
}: SocialProvidersProps) {
  const { providers, callbackUrl } = useAuthContext()
  const isClient = useIsClient()

  // A lista de provedores só existe depois da hidratação; sem o esqueleto, a
  // área do formulário salta quando ela chega.
  if (!isClient) {
    return (
      <div className="flex flex-col gap-2">
        <div className="h-[var(--control-h-md)] w-full rounded-[var(--control-r-md)] border" />
        <div className="h-[var(--control-h-md)] w-full rounded-[var(--control-r-md)] border" />
      </div>
    )
  }

  const socialProviders = getSocialProviders(providers)

  if (!Object.values(socialProviders).length) return null

  return (
    <>
      {Object.values(socialProviders).map(provider => {
        const brandLogo = BRAND_LOGOS[provider.id]

        return (
          <Button
            key={provider.id}
            type="button"
            variant="light"
            className="w-full gap-2"
            onClick={() => signIn(provider.id, { callbackUrl })}
          >
            {typeof brandLogo === 'string' ? (
              <img alt="" src={brandLogo} className="h-5" />
            ) : (
              brandLogo
            )}
            {label(provider.name)}
          </Button>
        )
      })}
    </>
  )
}
